import { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@ndd/ui";
import {
  AlertCircle,
  ChevronDown,
  Clipboard,
  FileText,
  Plus,
  RotateCcw,
  Sparkles,
  Square,
  Trash2,
  X,
} from "lucide-react";
import {
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactElement,
} from "react";
import type {
  SoapWorkbenchBedrockEffort,
  SoapWorkbenchBudgetTokens,
  SoapWorkbenchConfigResponse,
  SoapWorkbenchControl,
  SoapWorkbenchMaxOutputTokens,
  SoapWorkbenchModelDescriptor,
  SoapWorkbenchModelKey,
  SoapWorkbenchOpenAiEffort,
  SoapWorkbenchRequest,
} from "../../../../shared/soap-workbench";
import { SoapDraftRenderer } from "@/components/soap-draft-renderer";
import { fetchWithAuthRetry } from "@/lib/api";
import {
  createSoapWorkbenchState,
  soapWorkbenchReducer,
  type SoapWorkbenchForm,
  type SoapWorkbenchRun,
} from "@/lib/soap-workbench-state";
import { readSoapWorkbenchStream } from "@/lib/soap-workbench-stream";
import { AdminGuard, AdminNav } from "./shared";

const CONFIG_PATH = "/api/admin/soap-workbench/config";
const GENERATE_PATH = "/api/admin/soap-workbench/generate";

export function AdminSoapWorkbench(): ReactElement {
  return (
    <AdminGuard>
      <SoapWorkbenchLoader />
    </AdminGuard>
  );
}

function SoapWorkbenchLoader(): ReactElement {
  const { getToken } = useAuth();
  const [config, setConfig] = useState<SoapWorkbenchConfigResponse["data"] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetchWithAuthRetry(getToken, CONFIG_PATH, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          setUnavailable(true);
          return;
        }
        const body = await response.json() as SoapWorkbenchConfigResponse;
        setConfig(body.data);
      } catch {
        if (!controller.signal.aborted) {
          Sentry.captureException(new Error("SOAP workbench configuration request failed"), {
            tags: { surface: "soap_workbench", source: "client" },
          });
          setUnavailable(true);
        }
      }
    })();
    return () => controller.abort();
  }, [getToken]);

  if (unavailable) {
    return (
      <AdminPageFrame>
        <EmptyState
          icon={AlertCircle}
          title="SOAP workbench unavailable"
          description="This surface is not enabled in this environment."
        />
      </AdminPageFrame>
    );
  }
  if (!config) {
    return (
      <AdminPageFrame>
        <div className="space-y-4" aria-label="Loading SOAP workbench">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-52 w-full" />
        </div>
      </AdminPageFrame>
    );
  }
  return <SoapWorkbenchSurface config={config} />;
}

function AdminPageFrame({ children }: { children: ReactElement }): ReactElement {
  return (
    <main className="min-h-screen bg-background px-5 py-8 sm:px-8">
      <div className="mx-auto w-full max-w-[920px]">
        <AdminNav />
        {children}
      </div>
    </main>
  );
}

function SoapWorkbenchSurface({ config }: { config: SoapWorkbenchConfigResponse["data"] }): ReactElement {
  const { getToken } = useAuth();
  const [state, dispatch] = useReducer(soapWorkbenchReducer, config, createSoapWorkbenchState);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [templateOpen, setTemplateOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const abortRef = useRef<AbortController | null>(null);
  const transcriptRef = useRef<HTMLTextAreaElement>(null);
  const newestRunRef = useRef<HTMLButtonElement>(null);

  const activeRun = state.runs.find((run) => run.id === state.activeRunId) ?? null;
  useEffect(() => {
    if (!activeRun) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [activeRun]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      abortRef.current?.abort();
    };
  }, []);

  const selectedModel = config.models.find((model) => model.key === state.form.modelKey) ?? config.models[0];
  if (!selectedModel) return <AdminPageFrame><EmptyState title="No models available" description="The workbench model catalog is empty." /></AdminPageFrame>;
  const selection = state.form.selections[selectedModel.key];
  const transcriptNearLimit = state.form.transcript.length >= config.limits.transcriptWarningAt;
  const transcriptOverLimit = state.form.transcript.length > config.limits.transcript;

  function dispatchField(field: "transcript" | "appointmentNote" | "priorSoapNote" | "prompt" | "soapTemplate", value: string) {
    setValidationMessage(null);
    dispatch({ type: "field", field, value });
  }

  function updateSelection(
    modelKey: SoapWorkbenchModelKey,
    next: SoapWorkbenchForm["selections"][SoapWorkbenchModelKey],
  ) {
    setValidationMessage(null);
    dispatch({ type: "selection", modelKey, selection: next });
  }

  function validateForm(): string | null {
    if (!state.form.transcript.trim()) return "Add a Transcript before generating.";
    if (!state.form.prompt.trim()) return "Add a Prompt before generating.";
    if (!state.form.soapTemplate.trim()) return "Add a SOAP template before generating.";
    if (state.form.transcript.length > config.limits.transcript) return "Transcript is over the 200,000-character limit.";
    if (state.form.appointmentNote.length > config.limits.appointmentNote) return "Appointment note is over its 50,000-character limit.";
    if (state.form.priorSoapNote.length > config.limits.priorSoapNote) return "Prior SOAP Note is over its 100,000-character limit.";
    if (state.form.prompt.length > config.limits.prompt) return "Prompt is over its 50,000-character limit.";
    if (state.form.soapTemplate.length > config.limits.soapTemplate) return "SOAP template is over its 50,000-character limit.";
    const combined = state.form.transcript.length + state.form.appointmentNote.length + state.form.priorSoapNote.length + state.form.prompt.length + state.form.soapTemplate.length;
    if (combined > config.limits.combined) return "The combined inputs are over the 400,000-character limit.";
    return null;
  }

  async function generate(): Promise<void> {
    const error = validateForm();
    if (error) {
      setValidationMessage(error);
      if (!state.form.transcript.trim()) transcriptRef.current?.focus();
      return;
    }
    if (state.activeRunId) return;

    const runId = crypto.randomUUID();
    const sourceNames = [
      "Transcript",
      state.form.appointmentNoteEnabled ? "Appointment note" : null,
      state.form.priorSoapNoteEnabled ? "Prior SOAP Note" : null,
      state.form.soapTemplate === config.defaultSoapTemplate ? "V1 SOAP template" : "Edited SOAP template",
    ].filter((value): value is string => value !== null);
    const startedAt = Date.now();
    const endToEndStartedAt = performance.now();
    const endToEndElapsed = () => Math.max(0, performance.now() - endToEndStartedAt);
    dispatch({
      type: "start",
      run: {
        id: runId,
        modelLabel: selectedModel.label,
        effectiveSetting: describeControl(selection.control),
        maxOutputTokens: selection.maxOutputTokens,
        sourceSummary: sourceNames.join(" + "),
        wordCount: countWords(state.form.transcript),
        formRevision: state.formRevision,
        startedAt,
      },
    });
    setStatusMessage(`Run ${state.nextRunNumber} started.`);
    window.requestAnimationFrame(() => newestRunRef.current?.focus());

    const controller = new AbortController();
    abortRef.current = controller;
    const request: SoapWorkbenchRequest = {
      transcript: state.form.transcript,
      appointmentNote: state.form.appointmentNoteEnabled ? state.form.appointmentNote : "",
      priorSoapNote: state.form.priorSoapNoteEnabled ? state.form.priorSoapNote : "",
      prompt: state.form.prompt,
      soapTemplate: state.form.soapTemplate,
      modelKey: selectedModel.key,
      maxOutputTokens: selection.maxOutputTokens,
      control: selection.control,
    };

    try {
      const response = await fetchWithAuthRetry(getToken, GENERATE_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        cache: "no-store",
        signal: controller.signal,
      });
      if (!response.ok) {
        if (response.status !== 409 && response.status !== 429) {
          Sentry.captureException(new Error("SOAP workbench generation request rejected"), {
            tags: {
              surface: "soap_workbench",
              source: "client",
              status: String(response.status),
            },
          });
        }
        const message = response.status === 429
          ? "Too many workbench runs. Wait a few minutes and try again."
          : response.status === 409
            ? "Finish or cancel the current run before starting another."
            : "The draft could not be generated. Try again.";
        dispatch({ type: "failed", runId, endToEndDurationMs: endToEndElapsed(), message });
        setStatusMessage(`Run ${state.nextRunNumber} failed.`);
        return;
      }
      for await (const event of readSoapWorkbenchStream(response.body)) {
        switch (event.type) {
          case "started":
            dispatch({ type: "started", runId, effectiveSetting: event.effectiveSetting });
            break;
          case "text_delta":
            dispatch({ type: "delta", runId, delta: event.delta });
            break;
          case "completed":
            dispatch({
              type: "completed",
              runId,
              endToEndDurationMs: endToEndElapsed(),
              usage: event.usage,
              effectiveSetting: event.effectiveSetting,
            });
            setStatusMessage(`Run ${state.nextRunNumber} completed.`);
            break;
          case "failed":
            dispatch({ type: "failed", runId, endToEndDurationMs: endToEndElapsed(), message: event.message });
            setStatusMessage(`Run ${state.nextRunNumber} failed.`);
            break;
          case "heartbeat":
            break;
        }
      }
    } catch {
      if (controller.signal.aborted) {
        dispatch({ type: "cancelled", runId, endToEndDurationMs: endToEndElapsed() });
        setStatusMessage(`Run ${state.nextRunNumber} cancelled.`);
      } else {
        Sentry.captureException(new Error("SOAP workbench client request failed"), {
          tags: { surface: "soap_workbench", source: "client" },
        });
        dispatch({
          type: "failed",
          runId,
          endToEndDurationMs: endToEndElapsed(),
          message: "The model response could not be read. Try again.",
        });
        setStatusMessage(`Run ${state.nextRunNumber} failed.`);
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  function cancelActive(): void {
    if (!activeRun) return;
    abortRef.current?.abort();
    dispatch({ type: "cancelled", runId: activeRun.id, endToEndDurationMs: Date.now() - activeRun.startedAt });
    setStatusMessage(`Run ${activeRun.number} cancelled.`);
  }

  function clearEverything(): void {
    abortRef.current?.abort();
    abortRef.current = null;
    setValidationMessage(null);
    setStatusMessage("Workbench cleared.");
    setTemplateOpen(false);
    dispatch({ type: "clear", config });
    window.requestAnimationFrame(() => transcriptRef.current?.focus());
  }

  return (
    <AdminPageFrame>
      <div className="pb-28">
        <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sand-500">Clinical admin tool</p>
            <h1 className="mt-2 font-sans text-2xl font-semibold tracking-tight text-foreground">SOAP Prompt Workbench</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              Compare production models against pasted source material before the SOAP Draft workflow ships.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm"><Trash2 />Clear everything</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear the workbench?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes every source, prompt edit, and result from this tab. The action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep working</AlertDialogCancel>
                <AlertDialogAction asChild>
                  <Button variant="destructive" onClick={clearEverything}>Clear everything</Button>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </header>

        <Alert variant="warning" role="status" className="mb-6">
          <AlertCircle />
          <AlertDescription>
            Nothing here is saved or sent to Healthie. Leaving or refreshing clears every input and result.
          </AlertDescription>
        </Alert>

        <div className="space-y-6">
          <WorkbenchStep number="1" title="Sources" description="Paste the current visit transcript. Add optional context only when it helps the comparison.">
            <div className="space-y-4">
              <ClinicalField
                id="soap-workbench-transcript"
                label="Transcript"
                required
                help="Current visit transcript"
                count={state.form.transcript.length}
                limit={config.limits.transcript}
                warning={transcriptNearLimit}
                invalid={transcriptOverLimit}
              >
                <Textarea
                  ref={transcriptRef}
                  id="soap-workbench-transcript"
                  aria-describedby="soap-workbench-transcript-help soap-workbench-transcript-count"
                  aria-invalid={transcriptOverLimit}
                  value={state.form.transcript}
                  onChange={(event) => dispatchField("transcript", event.target.value)}
                  placeholder="Paste the current visit transcript…"
                  className="min-h-52 resize-y font-mono text-[13px] leading-5"
                  autoComplete="off"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                />
              </ClinicalField>
              <OptionalSource
                label="Appointment note"
                description="Scheduling context or intake note"
                enabled={state.form.appointmentNoteEnabled}
                onEnabled={(enabled) => dispatch({ type: "optional_source", source: "appointmentNote", enabled })}
              >
                <ClinicalField id="soap-workbench-appointment-note" label="Appointment note" count={state.form.appointmentNote.length} limit={config.limits.appointmentNote}>
                  <Textarea
                    id="soap-workbench-appointment-note"
                    aria-describedby="soap-workbench-appointment-note-count"
                    value={state.form.appointmentNote}
                    onChange={(event) => dispatchField("appointmentNote", event.target.value)}
                    className="min-h-28 resize-y"
                    autoComplete="off"
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                </ClinicalField>
              </OptionalSource>
              <OptionalSource
                label="Prior SOAP Note"
                description="Background and continuity only"
                enabled={state.form.priorSoapNoteEnabled}
                onEnabled={(enabled) => dispatch({ type: "optional_source", source: "priorSoapNote", enabled })}
              >
                <ClinicalField id="soap-workbench-prior-note" label="Prior SOAP Note" count={state.form.priorSoapNote.length} limit={config.limits.priorSoapNote}>
                  <Textarea
                    id="soap-workbench-prior-note"
                    aria-describedby="soap-workbench-prior-note-count"
                    value={state.form.priorSoapNote}
                    onChange={(event) => dispatchField("priorSoapNote", event.target.value)}
                    className="min-h-36 resize-y"
                    autoComplete="off"
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                </ClinicalField>
              </OptionalSource>
            </div>
          </WorkbenchStep>

          <WorkbenchStep number="2" title="Prompt" description="Edit the built-in V1 drafting prompt for this comparison.">
            <div className="space-y-4">
              <ClinicalField id="soap-workbench-prompt" label="Prompt" required count={state.form.prompt.length} limit={config.limits.prompt}>
                <div>
                  <div className="mb-2 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => dispatchField("prompt", config.defaultPrompt)}
                      disabled={state.form.prompt === config.defaultPrompt}
                    >
                      <RotateCcw />Reset prompt
                    </Button>
                  </div>
                  <Textarea
                    id="soap-workbench-prompt"
                    aria-describedby="soap-workbench-prompt-count"
                    value={state.form.prompt}
                    onChange={(event) => dispatchField("prompt", event.target.value)}
                    className="min-h-72 resize-y font-mono text-[13px] leading-5"
                    autoComplete="off"
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                </div>
              </ClinicalField>

              <div className="rounded-xl border border-border bg-muted/20">
                <Collapsible open={templateOpen} onOpenChange={setTemplateOpen}>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">SOAP template</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        The default V1 structure is included in every run. Open it to customize the exact order and fields.
                      </p>
                    </div>
                    <CollapsibleTrigger asChild>
                      <Button type="button" variant="outline" size="xs">
                        {templateOpen ? "Hide template" : "Edit template"}
                        <ChevronDown className={`transition-transform ${templateOpen ? "rotate-180" : ""}`} />
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="border-t border-border px-4 py-4">
                    <ClinicalField
                      id="soap-workbench-template"
                      label="SOAP template"
                      required
                      count={state.form.soapTemplate.length}
                      limit={config.limits.soapTemplate}
                    >
                      <div>
                        <div className="mb-2 flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => dispatchField("soapTemplate", config.defaultSoapTemplate)}
                            disabled={state.form.soapTemplate === config.defaultSoapTemplate}
                          >
                            <RotateCcw />Reset template
                          </Button>
                        </div>
                        <Textarea
                          id="soap-workbench-template"
                          aria-describedby="soap-workbench-template-count"
                          value={state.form.soapTemplate}
                          onChange={(event) => dispatchField("soapTemplate", event.target.value)}
                          className="min-h-64 resize-y font-mono text-[13px] leading-5"
                          autoComplete="off"
                          spellCheck={false}
                          autoCorrect="off"
                          autoCapitalize="off"
                        />
                      </div>
                    </ClinicalField>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </WorkbenchStep>

          <WorkbenchStep number="3" title="Model" description="Choose one approved production model and its provider-specific reasoning controls.">
            <ModelControls
              models={config.models}
              selectedModel={selectedModel}
              selection={selection}
              allSelections={state.form.selections}
              onModel={(modelKey) => dispatch({ type: "model", modelKey })}
              onSelection={updateSelection}
            />
          </WorkbenchStep>

          {validationMessage ? (
            <Alert variant="danger">
              <AlertCircle />
              <AlertDescription>{validationMessage}</AlertDescription>
            </Alert>
          ) : null}

          <section aria-labelledby="soap-workbench-results">
            <div className="mb-3 flex items-center justify-between">
              <h2 id="soap-workbench-results" className="font-sans text-lg font-semibold">Results</h2>
              {state.runs.length ? <span className="text-xs text-muted-foreground">Newest first</span> : null}
            </div>
            {state.runs.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No drafts yet"
                description="Add a transcript, choose a model, and generate the first comparison."
              />
            ) : (
              <div className="space-y-4">
                {state.runs.map((run, index) => (
                  <RunCard
                    key={run.id}
                    run={run}
                    currentRevision={state.formRevision}
                    now={now}
                    triggerRef={index === 0 ? newestRunRef : undefined}
                    onGenerate={() => void generate()}
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 px-5 py-4 shadow-[0_-8px_24px_rgba(41,37,36,0.06)] backdrop-blur sm:px-8">
          <div className="mx-auto flex w-full max-w-[920px] items-center justify-between gap-4">
            <p className="hidden text-xs text-muted-foreground sm:block">
              {activeRun ? `${activeRun.modelLabel} is generating…` : `${selectedModel.label} · ${describeControl(selection.control)} · ${selection.maxOutputTokens.toLocaleString()} max output`}
            </p>
            {activeRun ? (
              <Button variant="outline" onClick={cancelActive}><Square />Cancel · {formatDuration(now - activeRun.startedAt)}</Button>
            ) : (
              <Button variant="ai" onClick={() => void generate()} disabled={transcriptOverLimit} className="ml-auto">
                <Sparkles />Generate SOAP draft
              </Button>
            )}
          </div>
        </div>
        <p className="sr-only" aria-live="polite" aria-atomic="true">{statusMessage}</p>
      </div>
    </AdminPageFrame>
  );
}

function WorkbenchStep({
  number,
  title,
  description,
  children,
}: {
  number: string;
  title: string;
  description: string;
  children: ReactElement;
}): ReactElement {
  const headingId = `soap-workbench-step-${number}`;
  return (
    <section aria-labelledby={headingId}>
      <Card>
        <CardContent className="p-5 sm:p-6">
          <div className="mb-5 flex gap-3">
            <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-full bg-ai text-xs font-semibold text-ai-foreground">{number}</span>
            <div>
              <h2 id={headingId} className="font-sans text-base font-semibold text-foreground">{title}</h2>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">{description}</p>
            </div>
          </div>
          {children}
        </CardContent>
      </Card>
    </section>
  );
}

function ClinicalField({
  id,
  label,
  help,
  required = false,
  count,
  limit,
  warning = false,
  invalid = false,
  children,
}: {
  id: string;
  label: string;
  help?: string;
  required?: boolean;
  count: number;
  limit: number;
  warning?: boolean;
  invalid?: boolean;
  children: ReactElement;
}): ReactElement {
  return (
    <div>
      <div className="mb-2 flex items-end justify-between gap-3">
        <div>
          <label htmlFor={id} className="text-sm font-medium text-foreground">{label}{required ? <span className="text-danger"> *</span> : null}</label>
          {help ? <p id={`${id}-help`} className="mt-0.5 text-xs text-muted-foreground">{help}</p> : null}
        </div>
        <span
          id={`${id}-count`}
          className={invalid ? "text-xs font-medium text-danger" : warning ? "text-xs font-medium text-warning" : "text-xs text-muted-foreground"}
        >
          {count.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      {children}
    </div>
  );
}

function OptionalSource({
  label,
  description,
  enabled,
  onEnabled,
  children,
}: {
  label: string;
  description: string;
  enabled: boolean;
  onEnabled: (enabled: boolean) => void;
  children: ReactElement;
}): ReactElement {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
        <Button type="button" variant="ghost" size="xs" onClick={() => onEnabled(!enabled)}>
          {enabled ? <><X />Remove</> : <><Plus />Add</>}
        </Button>
      </div>
      {enabled ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}

function ModelControls({
  models,
  selectedModel,
  selection,
  allSelections,
  onModel,
  onSelection,
}: {
  models: readonly SoapWorkbenchModelDescriptor[];
  selectedModel: SoapWorkbenchModelDescriptor;
  selection: SoapWorkbenchForm["selections"][SoapWorkbenchModelKey];
  allSelections: SoapWorkbenchForm["selections"];
  onModel: (modelKey: SoapWorkbenchModelKey) => void;
  onSelection: (modelKey: SoapWorkbenchModelKey, selection: SoapWorkbenchForm["selections"][SoapWorkbenchModelKey]) => void;
}): ReactElement {
  const setMaxOutput = (maxOutputTokens: SoapWorkbenchMaxOutputTokens) => {
    let control = selection.control;
    if (control.kind === "bedrock" && control.mode === "budget" && control.budgetTokens >= maxOutputTokens) {
      const bedrockModel = selectedModel.provider === "bedrock" ? selectedModel : null;
      const valid = bedrockModel?.control.budgetOptions.filter((budget) => budget < maxOutputTokens) ?? [];
      control = { kind: "bedrock", mode: "budget", budgetTokens: valid.at(-1) ?? 1_024 };
    }
    onSelection(selectedModel.key, { maxOutputTokens, control });
  };

  return (
    <div className="space-y-5">
      <div>
        <p id="soap-workbench-model-label" className="mb-2 text-sm font-medium">Model</p>
        <Tabs value={selectedModel.key} onValueChange={(value) => onModel(value as SoapWorkbenchModelKey)} aria-labelledby="soap-workbench-model-label">
          <TabsList className="grid h-auto w-full grid-cols-2 p-1">
            {models.map((model) => <TabsTrigger key={model.key} value={model.key}>{model.label}</TabsTrigger>)}
          </TabsList>
          {models.map((model) => <TabsContent className="sr-only" key={model.key} value={model.key}>{model.label} selected.</TabsContent>)}
        </Tabs>
      </div>

      {selectedModel.provider === "bedrock" ? (
        <BedrockControls
          model={selectedModel}
          selection={selection}
          onControl={(control) => onSelection(selectedModel.key, { ...selection, control })}
        />
      ) : (
        <OpenAiControls
          model={selectedModel}
          selection={allSelections[selectedModel.key]}
          onControl={(control) => onSelection(selectedModel.key, { ...selection, control })}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
        <div>
          <label htmlFor="soap-workbench-max-output" className="mb-2 block text-sm font-medium">Max output</label>
          <Select value={String(selection.maxOutputTokens)} onValueChange={(value) => setMaxOutput(Number(value) as SoapWorkbenchMaxOutputTokens)}>
            <SelectTrigger id="soap-workbench-max-output"><SelectValue /></SelectTrigger>
            <SelectContent>
              {selectedModel.maxOutputOptions.map((value) => <SelectItem key={value} value={String(value)}>{value.toLocaleString()} tokens</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs leading-5 text-muted-foreground">Provider-total ceiling. Reasoning tokens count toward this limit.</p>
      </div>
    </div>
  );
}

function BedrockControls({
  model,
  selection,
  onControl,
}: {
  model: Extract<SoapWorkbenchModelDescriptor, { provider: "bedrock" }>;
  selection: SoapWorkbenchForm["selections"][SoapWorkbenchModelKey];
  onControl: (control: SoapWorkbenchControl) => void;
}): ReactElement {
  const control: Extract<SoapWorkbenchControl, { kind: "bedrock" }> = selection.control.kind === "bedrock"
    ? selection.control
    : { kind: "bedrock", ...model.control.default };
  const mode = "mode" in control ? control.mode : "adaptive";
  function setMode(next: string) {
    if (next === "off") onControl({ kind: "bedrock", mode: "off" });
    else if (next === "budget") onControl({ kind: "bedrock", mode: "budget", budgetTokens: 2_048 });
    else onControl({ kind: "bedrock", mode: "adaptive", effort: "high" });
  }
  return (
    <div className="space-y-4">
      <div>
        <p id="soap-workbench-thinking-mode" className="mb-2 text-sm font-medium">Thinking</p>
        <Tabs value={mode} onValueChange={setMode} aria-labelledby="soap-workbench-thinking-mode">
          <TabsList className="grid h-auto w-full grid-cols-3 p-1">
            <TabsTrigger value="off">Off</TabsTrigger>
            <TabsTrigger value="adaptive">Adaptive</TabsTrigger>
            <TabsTrigger value="budget">Budget</TabsTrigger>
          </TabsList>
          <TabsContent className="sr-only" value="off">Thinking is off.</TabsContent>
          <TabsContent className="sr-only" value="adaptive">Adaptive thinking selected.</TabsContent>
          <TabsContent className="sr-only" value="budget">Thinking budget selected.</TabsContent>
        </Tabs>
      </div>
      {control.kind === "bedrock" && control.mode === "adaptive" ? (
        <ChoiceTabs
          label="Adaptive effort"
          value={control.effort}
          values={model.control.adaptiveEfforts}
          onValue={(value) => onControl({ kind: "bedrock", mode: "adaptive", effort: value as SoapWorkbenchBedrockEffort })}
        />
      ) : null}
      {control.kind === "bedrock" && control.mode === "budget" ? (
        <ChoiceTabs
          label="Thinking budget"
          value={String(control.budgetTokens)}
          values={model.control.budgetOptions.filter((budget) => budget < selection.maxOutputTokens).map(String)}
          format={(value) => Number(value).toLocaleString()}
          onValue={(value) => onControl({ kind: "bedrock", mode: "budget", budgetTokens: Number(value) as SoapWorkbenchBudgetTokens })}
        />
      ) : null}
    </div>
  );
}

function OpenAiControls({
  model,
  selection,
  onControl,
}: {
  model: Extract<SoapWorkbenchModelDescriptor, { provider: "openai" }>;
  selection: SoapWorkbenchForm["selections"][SoapWorkbenchModelKey];
  onControl: (control: SoapWorkbenchControl) => void;
}): ReactElement {
  const effort = selection.control.kind === "openai" ? selection.control.reasoningEffort : "medium";
  return (
    <ChoiceTabs
      label="Reasoning effort"
      value={effort}
      values={model.control.reasoningEfforts}
      onValue={(value) => onControl({ kind: "openai", reasoningEffort: value as SoapWorkbenchOpenAiEffort })}
    />
  );
}

function ChoiceTabs({
  label,
  value,
  values,
  format = capitalize,
  onValue,
}: {
  label: string;
  value: string;
  values: readonly string[];
  format?: (value: string) => string;
  onValue: (value: string) => void;
}): ReactElement {
  const id = `soap-workbench-choice-${label.toLowerCase().replaceAll(" ", "-")}`;
  return (
    <div>
      <p id={id} className="mb-2 text-sm font-medium">{label}</p>
      <Tabs value={value} onValueChange={onValue} aria-labelledby={id}>
        <TabsList className="flex h-auto w-full flex-wrap p-1">
          {values.map((choice) => <TabsTrigger className="min-w-20 flex-1" key={choice} value={choice}>{format(choice)}</TabsTrigger>)}
        </TabsList>
        {values.map((choice) => <TabsContent className="sr-only" key={choice} value={choice}>{format(choice)} selected.</TabsContent>)}
      </Tabs>
    </div>
  );
}

function RunCard({
  run,
  currentRevision,
  now,
  triggerRef,
  onGenerate,
}: {
  run: SoapWorkbenchRun;
  currentRevision: number;
  now: number;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  onGenerate: () => void;
}): ReactElement {
  const [open, setOpen] = useState(true);
  const changed = run.formRevision !== currentRevision;
  const duration = run.endToEndDurationMs ?? Math.max(0, now - run.startedAt);
  const badge = run.status === "completed" ? { variant: "success" as const, text: "Completed" }
    : run.status === "failed" ? { variant: "danger" as const, text: "Failed" }
      : run.status === "cancelled" ? { variant: "neutral" as const, text: "Cancelled" }
        : { variant: "ai" as const, text: "Generating" };
  const facts = [
    run.status === "generating" ? `Elapsed ${formatDuration(duration)}` : null,
    run.status === "failed" ? `Ended after ${formatDuration(duration)}` : null,
    run.status === "cancelled" ? `Cancelled after ${formatDuration(duration)}` : null,
    run.usage.inputTokens !== null && run.usage.outputTokens !== null
      ? `${run.usage.inputTokens.toLocaleString()} in / ${run.usage.outputTokens.toLocaleString()} out`
      : null,
    run.wordCount ? `${run.wordCount.toLocaleString()} transcript words` : null,
  ].filter((fact): fact is string => fact !== null);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <h3>
          <CollapsibleTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              className="flex w-full items-start justify-between gap-4 rounded-t-xl p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-6"
            >
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-sans text-base font-semibold">Run {run.number}</span>
                  <Badge variant={badge.variant}>{badge.text}</Badge>
                </div>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  {run.modelLabel} · {run.effectiveSetting} · {run.maxOutputTokens.toLocaleString()} max · {run.sourceSummary}
                </p>
                {run.status === "completed" ? (
                  <p className="mt-1 text-xs font-medium text-foreground">
                    End-to-end processing time: {formatDuration(duration)}
                  </p>
                ) : null}
              </div>
              <ChevronDown className={`mt-1 size-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
        </h3>
        <CollapsibleContent>
          <CardContent className="px-5 pb-5 sm:px-6 sm:pb-6">
            {run.status === "failed" ? (
              <Alert variant="danger"><AlertCircle /><AlertDescription>{run.errorMessage}</AlertDescription></Alert>
            ) : run.status === "cancelled" ? (
              <p className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">Cancelled after {formatDuration(duration)}.</p>
            ) : (
              <Tabs defaultValue="rendered">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <TabsList><TabsTrigger value="rendered">Rendered</TabsTrigger><TabsTrigger value="raw">Raw</TabsTrigger></TabsList>
                  <Button
                    variant="outline"
                    size="xs"
                    disabled={!run.output}
                    onClick={() => void navigator.clipboard.writeText(run.output)
                      .then(() => toast.success("Copied"))
                      .catch(() => {
                        Sentry.captureException(new Error("SOAP workbench clipboard write failed"), {
                          tags: { surface: "clipboard", source: "client" },
                        });
                        toast.error("Copy failed");
                      })}
                  >
                    <Clipboard />Copy draft
                  </Button>
                </div>
                <TabsContent value="rendered" className="rounded-lg border border-border bg-background p-4 sm:p-5">
                  {run.output ? <SoapDraftRenderer markdown={run.output} /> : <GeneratingDraft />}
                </TabsContent>
                <TabsContent value="raw">
                  <pre className="max-h-[640px] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/30 p-4 text-xs leading-5">{run.output || "Waiting for the model…"}</pre>
                </TabsContent>
              </Tabs>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>
                {facts.join(" · ")}
              </span>
              {run.status !== "generating" ? (
                <Button variant="ghost" size="xs" onClick={onGenerate}>
                  <Sparkles />{changed ? "Generate current inputs" : "Try again"}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function GeneratingDraft(): ReactElement {
  return (
    <div className="space-y-3" aria-hidden="true">
      <div className="flex items-center gap-2 text-sm text-ai"><Sparkles className="size-4" />Drafting…</div>
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
    </div>
  );
}

function describeControl(control: SoapWorkbenchControl): string {
  if (control.kind === "openai") return `Reasoning ${control.reasoningEffort}`;
  if (control.mode === "off") return "Thinking off";
  if (control.mode === "adaptive") return `Adaptive thinking · ${control.effort}`;
  return `Thinking budget · ${control.budgetTokens.toLocaleString()} tokens`;
}

function countWords(value: string): number {
  const trimmed = value.trim();
  return trimmed ? trimmed.split(/\s+/u).length : 0;
}

function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
